import { useState, useEffect, useRef } from 'react';

export interface ColumnSetting {
  key: string;
  title: string;         // original title
  displayTitle: string;  // possibly renamed
  visible: boolean;
  /** Vị trí hiển thị = chỉ số trong mảng `settings`. Luôn dày và luôn khớp với tập cột hiện tại. */
  order: number;
  width?: number;
}

export interface ColumnSettingSource {
  key: string;
  title: string;
  width?: number;
  /**
   * Cột khởi tạo ở trạng thái ẨN (người dùng vẫn bật lại được trong drawer "Tuỳ chỉnh cột").
   * Chỉ quyết định trạng thái BAN ĐẦU và trạng thái sau khi Đặt lại — lựa chọn đã lưu của
   * người dùng luôn thắng cờ này. Không khai = giữ nguyên hành vi cũ (hiện mặc định).
   */
  defaultHidden?: boolean;
}

/**
 * Bản lưu trong localStorage. Thứ tự cột = vị trí trong `columns`, KHÔNG phải số `order`.
 *
 * Bản cũ lưu `order` là chỉ số trong danh sách cột cấu hình được. Mỗi lần thêm/bớt cột,
 * cùng một số `order` lại trỏ sang cột khác — layout đã lưu của người dùng bị xô lệch.
 * Lưu theo danh sách key thì thêm/bớt cột không đụng tới thứ tự các cột còn lại.
 *
 * `displayTitle` chỉ ghi khi người dùng thực sự đổi tên; nhờ vậy đổi tiêu đề ở source
 * tự lan tới người dùng cũ mà không cần đụng bản lưu.
 */
interface StoredColumnEntry {
  key: string;
  visible: boolean;
  displayTitle?: string;
  width?: number;
}

interface StoredColumnLayout {
  version: number;
  columns: StoredColumnEntry[];
}

/** Trạng thái hiện/ẩn lúc khởi tạo. Tách riêng để 3 nhánh (merge, init, reset) không lệch nhau. */
function initialVisible(col: ColumnSettingSource): boolean {
  return !col.defaultHidden;
}

function toStoredEntry(setting: ColumnSetting): StoredColumnEntry {
  return {
    key: setting.key,
    visible: setting.visible,
    ...(setting.displayTitle !== setting.title ? { displayTitle: setting.displayTitle } : {}),
    ...(setting.width !== undefined ? { width: setting.width } : {}),
  };
}

interface LegacyColumnSetting {
  key?: unknown;
  title?: unknown;
  displayTitle?: unknown;
  visible?: unknown;
  order?: unknown;
  width?: unknown;
}

/**
 * Bản lưu cũ là mảng `ColumnSetting[]` có `order`, không có version. Coi như version 0 và
 * dựng lại thứ tự từ `order` — người dùng chưa từng bị đụng layout thì không mất gì.
 */
function migrateLegacyLayout(parsed: LegacyColumnSetting[]): StoredColumnLayout {
  const ordered = [...parsed].sort((left, right) => (
    (typeof left.order === 'number' ? left.order : 0) - (typeof right.order === 'number' ? right.order : 0)
  ));
  return {
    version: 0,
    columns: ordered
      .filter((entry): entry is LegacyColumnSetting & { key: string } => typeof entry.key === 'string')
      .map((entry) => ({
        key: entry.key,
        visible: entry.visible !== false,
        // Chỉ giữ khi đúng là người dùng đổi tên, không phải tiêu đề source cũ.
        ...(typeof entry.displayTitle === 'string' && entry.displayTitle !== entry.title
          ? { displayTitle: entry.displayTitle }
          : {}),
        ...(typeof entry.width === 'number' ? { width: entry.width } : {}),
      })),
  };
}

function readStoredLayout(storageKey: string): StoredColumnLayout | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return migrateLegacyLayout(parsed as LegacyColumnSetting[]);
    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as StoredColumnLayout).columns)) {
      const layout = parsed as StoredColumnLayout;
      return {
        version: typeof layout.version === 'number' ? layout.version : 0,
        columns: layout.columns.filter((entry) => entry && typeof entry.key === 'string'),
      };
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * Ghép tập cột hiện tại với bản lưu.
 *
 * - Lệch version → GIỮ hiện/ẩn và tên tự đặt theo từng key, nhưng BỎ thứ tự lẫn độ rộng
 *   đã lưu. Cả hai thứ đó là "bố cục mặc định của bảng": bump version chính là cách duy
 *   nhất để đổi bố cục có chủ đích mà người dùng cũ cũng nhận được.
 * - Cùng version → giữ thứ tự đã lưu; cột mới chèn ngay sau hàng xóm liền trước của nó
 *   trong source, chứ không dồn xuống cuối bảng.
 */
function buildSettings(
  initialColumns: ColumnSettingSource[],
  stored: StoredColumnLayout | null,
  layoutVersion: number,
  forceSourceOrder = false,
): ColumnSetting[] {
  const sourceByKey = new Map(initialColumns.map((col) => [col.key, col]));
  const storedByKey = new Map((stored?.columns ?? []).map((entry) => [entry.key, entry]));
  const keepStoredOrder = !forceSourceOrder && stored != null && stored.version === layoutVersion;

  const orderedKeys: string[] = [];
  if (keepStoredOrder) {
    const seen = new Set<string>();
    for (const entry of stored.columns) {
      if (!sourceByKey.has(entry.key) || seen.has(entry.key)) continue;
      seen.add(entry.key);
      orderedKeys.push(entry.key);
    }
    initialColumns.forEach((col, index) => {
      if (seen.has(col.key)) return;
      let anchor = -1;
      for (let i = index - 1; i >= 0; i -= 1) {
        const at = orderedKeys.indexOf(initialColumns[i].key);
        if (at !== -1) { anchor = at; break; }
      }
      seen.add(col.key);
      orderedKeys.splice(anchor + 1, 0, col.key);
    });
  } else {
    for (const col of initialColumns) orderedKeys.push(col.key);
  }

  return orderedKeys.map((key, index) => {
    const source = sourceByKey.get(key)!;
    const saved = storedByKey.get(key);
    return {
      key,
      title: source.title,
      displayTitle: saved?.displayTitle ?? source.title,
      visible: saved ? saved.visible !== false : initialVisible(source),
      order: index,
      width: (keepStoredOrder ? saved?.width : undefined) ?? source.width,
    };
  });
}

export function useColumnSettings(
  tableKey: string,
  initialColumns: ColumnSettingSource[],
  scope?: { userId?: string; tenantId?: string },
  /**
   * Tăng số này khi cố ý đổi bố cục cột mặc định của bảng (vd chuyển một cột sang vị trí
   * khác). Người dùng đã có bản lưu sẽ nhận thứ tự mới mà vẫn giữ hiện/ẩn + tên tự đặt.
   * Không khai = 0 = không đụng gì tới các bảng khác.
   */
  layoutVersion = 0,
) {
  const scopeKey = scope?.tenantId && scope?.userId
    ? `${scope.tenantId}_${scope.userId}`
    : 'default';
  const storageKey = `erp_col_${scopeKey}_${tableKey}`;

  const [settings, setSettings] = useState<ColumnSetting[]>(
    () => buildSettings(initialColumns, readStoredLayout(storageKey), layoutVersion),
  );
  const layoutVersionRef = useRef(layoutVersion);

  // Tiêu đề cột có thể đổi theo runtime (vd cột sản phẩm đổi theo kênh đang lọc — TCR-391).
  // settings khởi tạo một lần nên phải đồng bộ lại, nếu không tiêu đề cũ sẽ đứng yên.
  // Tên do người dùng tự đặt vẫn được giữ.
  const titleSignature = initialColumns.map((col) => `${col.key}:${col.title}`).join('|');
  useEffect(() => {
    setSettings((prev) => {
      let changed = false;
      const next = prev.map((setting) => {
        const source = initialColumns.find((col) => col.key === setting.key);
        if (!source || source.title === setting.title) return setting;

        changed = true;
        const userRenamed = setting.displayTitle !== setting.title;
        return {
          ...setting,
          title: source.title,
          displayTitle: userRenamed ? setting.displayTitle : source.title,
        };
      });
      return changed ? next : prev;
    });
    // initialColumns là mảng mới mỗi lần render — theo dõi qua signature để không lặp vô hạn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleSignature]);

  // Có bảng dựng cột động (thêm/bớt theo bộ lọc). Ghép lại trên chính state đang có để
  // cột mới được chèn đúng chỗ mà thao tác kéo-thả trong phiên không bị mất.
  // Bump layoutVersion: bỏ thứ tự/độ rộng đã lưu (kể cả khi component không unmount —
  // HMR/Vite từng ghi version mới kèm thứ tự cũ nên bump không có tác dụng).
  const keySignature = initialColumns.map((col) => col.key).join('|');
  useEffect(() => {
    const versionChanged = layoutVersionRef.current !== layoutVersion;
    layoutVersionRef.current = layoutVersion;
    setSettings((prev) => {
      const prevKeys = prev.map((setting) => setting.key).join('|');
      if (prevKeys === keySignature && !versionChanged) return prev;
      return buildSettings(
        initialColumns,
        { version: layoutVersion, columns: prev.map(toStoredEntry) },
        layoutVersion,
        versionChanged,
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keySignature, layoutVersion]);

  useEffect(() => {
    const payload: StoredColumnLayout = { version: layoutVersion, columns: settings.map(toStoredEntry) };
    localStorage.setItem(storageKey, JSON.stringify(payload));
    // `layoutVersion` đọc từ render hiện tại. Không đưa vào deps: khi bump version
    // settings chưa đổi thì ghi version mới kèm thứ tự cũ sẽ "đóng băng" layout cũ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, storageKey]);

  const setVisible = (key: string, visible: boolean) => {
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, visible } : s)));
  };

  const setDisplayTitle = (key: string, displayTitle: string) => {
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, displayTitle } : s)));
  };

  const reorder = (fromIndex: number, toIndex: number) => {
    setSettings((prev) => {
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next.map((s, i) => ({ ...s, order: i }));
    });
  };

  const setWidth = (key: string, width?: number) => {
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, width } : s)));
  };

  // Đặt lại = về đúng mặc định của màn, KHÔNG phải "hiện tất cả": cột khai `defaultHidden`
  // phải ẩn lại y như lần đầu vào trang.
  const reset = () => {
    localStorage.removeItem(storageKey);
    setSettings(buildSettings(initialColumns, null, layoutVersion));
  };

  return { settings, setVisible, setDisplayTitle, setWidth, reorder, reset };
}
